import React, { useState, useEffect, useMemo } from 'react';
import { formatEmployeeNameForClient } from '../components/booking/utils/EmployeeNameUtils';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { formatCurrency } from '../../../../utils/helpers';
import DeluxeRemainingBalance from '../components/payments/deluxe/DeluxeRemainingBalance';
import { useAuth } from '../../../../context/AuthContext';
import AppointmentActions from '../components/appointments/AppointmentActions';
import PackageDetails from '../components/payments/package-details/PackageDetails';
import PaymentSummary from '../components/payments/payment-summary/PaymentSummary';
import ServiceDetails from '../components/services/ServiceDetails';
import EmployeeRating from '../../../../company-management/components/ratings/EmployeeRating';
import { toRatingDate } from '../../../../company-management/components/ratings/ratingDate';
import RescheduleDialog from '../components/appointments/reschedule/RescheduleDialog';
import { findCompanyBySlug, getCompanySlug } from '../../../../client-experience/utils/companyAccess';
import { useStorefrontAuth } from '../../../auth/context/StorefrontAuthContext';
import {
  getAppointmentUrlSegment,
  getStorefrontAppointmentDetailPath,
  isFirestoreAppointmentDocId,
} from '../../utils/clientPortalPaths';
import { getAppointmentDisplayMeta } from '../../../../utils/appointmentWorkflow';
import {
  resolveClientPortalActiveAppointmentSnapshot,
  resolveClientPortalArchiveAppointmentSnapshot,
} from '../../utils/resolveClientPortalAppointmentSnapshot';
import { FormSkeleton } from '../../../../components/loading/skeletons';
import { getPublicCompanyRecord } from '../../../../api/companyRecords';
import { getPublicPendingAppointmentOrNull } from '../../../../api/storefront/storefront';
import './styles/appointment-details/AppointmentDetails.css';

const convertToDate = (dateValue) => {
  if (!dateValue) return null;
  
  try {
    // If it's already a Date
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    // If it's a Firestore Timestamp
    if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    
    // If it's a timestamp object with seconds
    if (dateValue?.seconds) {
      return new Date(dateValue.seconds * 1000);
    }
    
    // If it's a string or number
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    console.error('Error converting date:', error, dateValue);
    return null;
  }
};

const AppointmentDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId, companyUrl } = useParams();
  const { companyData: authCompanyData } = useAuth();
  const { companyData: storefrontCompanyData, storefrontClient } = useStorefrontAuth();
  const companyData = storefrontCompanyData || authCompanyData;
  const companySlug = getCompanySlug(companyData) || companyUrl;

  const clientScope = useMemo(() => ({
    clientId: storefrontClient?.id || storefrontClient?.uid || '',
    clientEmail: (storefrontClient?.email || '').trim(),
  }), [storefrontClient?.id, storefrontClient?.uid, storefrontClient?.email]);
  const [appointment, setAppointment] = useState(null);
  const [staffDetails, setStaffDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [isLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const getCompanyIdForPortal = async () => {
    if (companyData?.id) return companyData.id;
    const storedCompanyId = localStorage.getItem('clientCompanyId');
    if (storedCompanyId) return storedCompanyId;
    if (!companyUrl) return null;
    const company = await findCompanyBySlug(companyUrl);
    return company?.id || null;
  };

  const fetchPendingAppointment = async (companyId, pendingId) => {
    if (!companyId || !pendingId) return null;
    const pending = await getPublicPendingAppointmentOrNull({
      companyId,
      pendingId,
    });
    if (!pending?.appointment) return null;
    const pendingData = pending.appointment;
    const dateTime = convertToDate(pendingData.dateTime);
    return {
      ...pendingData,
      id: pendingData.appointmentId || pending.appointmentId || pending.id,
      companyId,
      dateTime: dateTime || new Date(),
      serviceName: pendingData.serviceName || pendingData?.selectedService?.name || 'Service',
      employeeName:
        pendingData.employeeName ||
        pendingData?.employee?.employeeName ||
        pendingData?.employee?.name ||
        'Staff Member',
      paymentMethod: pendingData.paymentMethod || 'authorize',
      paymentStatus: pendingData.paymentStatus || 'pending',
      status: pendingData.status || 'pending',
      isPendingRecord: pending.status !== 'completed',
      pendingAppointmentId: pending.id,
    };
  };

  const hasPackage = useMemo(() => {
    if (!appointment) return false;
    // Only treat as package if the appointment actually selected/contains a package
    return Boolean(
      appointment.isPackage ||
      appointment.packageId ||
      appointment.packageName ||
      (appointment.package && Object.keys(appointment.package || {}).length > 0) ||
      (appointment.selectedPackage && Object.keys(appointment.selectedPackage || {}).length > 0)
    );
  }, [appointment]);

  const getPackageDetails = useMemo(() => {
    if (!appointment || !hasPackage) return null;

    // Get service data
    const serviceData = appointment.serviceData;
    console.log('Service data for package details:', {
      serviceData,
      packages: serviceData?.packages,
      hasPackage,
      appointmentPackage: appointment.package,
      isPackage: appointment.isPackage,
      packageName: appointment.packageName,
      selectedPackageName: appointment.selectedPackage?.name,
      serviceDataPackageName: serviceData?.packages?.[0]?.name
    });
    
    // Get package name from all possible sources
    const packageName = appointment.packageName || 
                       appointment.selectedPackage?.name || 
                       appointment.package?.name || 
                       serviceData?.packages?.[0]?.name || 
                       'Package';
    
    return {
      packages: serviceData?.packages || [],
      name: packageName,
      price: appointment.packagePrice || 
             appointment.selectedPackage?.price || 
             appointment.package?.price || 
             serviceData?.packages?.[0]?.price ||
             appointment.price || 0,
      duration: appointment.selectedPackage?.duration || 
                appointment.package?.duration || 
                serviceData?.packages?.[0]?.duration ||
                appointment.duration || 60,
      description: appointment.selectedPackage?.description || 
                  appointment.package?.description || 
                  serviceData?.packages?.[0]?.description ||
                  appointment.description || '',
      isPackage: true
    };
  }, [appointment, hasPackage]);

  const fetchAppointment = async () => {
    try {
      const companyId = await getCompanyIdForPortal();
      if (!companyId || !appointmentId) {
        throw new Error('Missing required information');
      }

      let appointmentData = null;
      let serviceData = null;

      const appointmentSnap = await resolveClientPortalActiveAppointmentSnapshot(
        companyId,
        appointmentId,
        clientScope
      );

      if (appointmentSnap && appointmentSnap.exists()) {
          const data = appointmentSnap.data();
          
          // Convert dates
          const dateTime = convertToDate(data.dateTime);
          if (dateTime) {

          // Fetch service details from serviceTags collection
          if (data.serviceId) {
            try {
              const service = await getPublicCompanyRecord({
                companyId,
                kind: 'services',
                id: data.serviceId,
              });
              if (service) {
                serviceData = service;
              }
            } catch (error) {
              console.warn('Could not load service details', error);
            }
          }

          // Fetch addon details if they exist
          let processedAddons = [];
          const sourceAddons = (Array.isArray(data.addons) && data.addons.length > 0)
            ? data.addons
            : (Array.isArray(data.selectedAddons) ? data.selectedAddons : []);
          if (sourceAddons.length > 0) {
            const serviceAddons = Array.isArray(serviceData?.addOns) ? serviceData.addOns : [];
            for (const addon of sourceAddons) {
              const addonData = serviceAddons.find((entry) => entry.id === addon.id);
              if (addonData) {
                processedAddons.push({
                  ...addon,
                  ...addonData,
                });
              } else {
                processedAddons.push(addon);
              }
            }
          }

          // Calculate total price including addons (values are already in dollars)
          const baseServicePrice = (data.paymentMethod === 'authorize' && data.priceBreakdown?.basePrice)
            ? data.priceBreakdown.basePrice
            : (data.packagePrice || data.selectedPackage?.price || serviceData?.price || data.price || data.servicePrice || data.appointmentPrice || 0);
          const addonsTotal = processedAddons.reduce((sum, addon) => sum + (addon.price || 0), 0);
          const basePrice = baseServicePrice + addonsTotal;
          
          // Get tax settings from service and tax details
          const taxDetails = data.taxDetails || {};
          const taxSettings = {
            isEnabled: serviceData?.taxSettings?.isEnabled || Boolean(taxDetails.taxRate),
            serviceRate: serviceData?.taxSettings?.serviceRate || taxDetails.taxRate
          };
          const isTaxEnabled = taxSettings.isEnabled;
          const serviceTaxRate = taxSettings.serviceRate;
          
          // Calculate tax amounts
          const baseTax = (data.paymentMethod === 'authorize' && typeof data.taxAmount === 'number')
            ? data.taxAmount
            : (isTaxEnabled ? basePrice * (serviceTaxRate / 100) : 0);
          let totalPrice = basePrice + baseTax;

          // Get deposit settings from service
          const depositSettings = serviceData?.depositSettings || data.depositSettings || {};
          const isDepositEnabled = depositSettings.isEnabled === true;
          const depositType = depositSettings.type || 'percentage';
          const depositValue = depositSettings.amount || 20;

          // Calculate deposit amount based on settings
          let depositBaseAmount = 0;
          if (isDepositEnabled) {
            switch (depositType) {
              case 'fixed':
                depositBaseAmount = depositValue;
                break;
              case 'percentage':
                depositBaseAmount = basePrice * (depositValue / 100);
                break;
              case 'duration':
                const totalDuration = parseInt(data.duration) || 60;
                depositBaseAmount = (totalDuration / 60) * depositValue;
                break;
              default:
                depositBaseAmount = basePrice * 0.20;
            }
          }

          // Apply deposit min/max limits if set
          if (depositSettings.minimumAmount && depositBaseAmount < depositSettings.minimumAmount) {
            depositBaseAmount = depositSettings.minimumAmount;
          }
          if (depositSettings.maximumAmount && depositBaseAmount > depositSettings.maximumAmount) {
            depositBaseAmount = depositSettings.maximumAmount;
          }

          // No deposit tax
          const depositTotal = depositBaseAmount;

          // Calculate remaining balance
          const remainingBaseAmount = basePrice - depositBaseAmount;
          const remainingTax = baseTax;
          let remainingBalance = totalPrice - depositTotal;

          // Create price breakdown structure (defaults)
          let priceBreakdown = {
            basePrice: basePrice,
            baseTax: baseTax,
            baseTotal: totalPrice,
            deposit: {
              baseAmount: depositBaseAmount,
              tax: 0,
              total: depositTotal,
              status: data.depositStatus || 'pending'
            },
            remaining: {
              baseAmount: remainingBaseAmount,
              tax: remainingTax,
              total: remainingBalance
            }
          };

          // Authorize-specific override: prefer stored totals/breakdown from finalization
          if (data.paymentMethod === 'authorize') {
            const storedTotal = parseFloat(data.totalPrice || 0);
            const storedDepositTotal = parseFloat(data.deposit?.total || data.deposit?.amount || 0);
            const storedBalanceTotal = parseFloat(data.balancePayment?.totalWithTax || 0);
            if (storedTotal > 0) {
              totalPrice = storedTotal;
            }
            if (storedDepositTotal > 0) {
              // Recompute remaining using stored values when present
              remainingBalance = storedBalanceTotal || Math.max(0, totalPrice - storedDepositTotal);
              priceBreakdown = {
                basePrice: basePrice,
                baseTax: baseTax,
                baseTotal: totalPrice,
                deposit: {
                  baseAmount: storedDepositTotal,
                  tax: 0,
                  total: storedDepositTotal,
                  status: data.depositStatus || data.deposit?.status || 'pending'
                },
                remaining: {
                  baseAmount: Math.max(0, (basePrice + baseTax) - storedDepositTotal),
                  tax: baseTax,
                  total: remainingBalance
                }
              };
            }
          }

          // Get deposit information and determine payment status
          const depositAmount = data.deposit?.amount || data.deposit?.total || depositBaseAmount;
          // Normalize deposit status for authorize payments
          const rawDepositStatus = data.depositStatus || data.deposit?.status || 'pending';
          const depositStatus = rawDepositStatus === 'deposit_paid' ? 'paid' : rawDepositStatus;
          const paymentStatus = data.paymentStatus || 'pending';
          
          // Determine payment states based on separate statuses
          const depositPaid = depositStatus === 'paid';
          const balancePaid = paymentStatus === 'paid';
          
          // Correct payment status if needed
          const correctedPaymentStatus = remainingBalance <= 0 || balancePaid ? 'paid' : 'pending';

          // Determine payment method based on deposit payment method
          const paymentMethodToUse = data.paymentMethod || 'authorize';

          appointmentData = {
            id: appointmentSnap.id,
            ...data,
            dateTime,
            companyId: companyId,
            employeeId: data.employeeId || data.employee?.id,
            employeeName: data.employeeName || data.employee?.name || staffDetails?.name || 'Staff Member',
            clientId: data.clientId || localStorage.getItem('clientId'),
            clientName: data.clientName || 
              `${data.clientFirstName || ''} ${data.clientLastName || ''}`.trim() ||
              localStorage.getItem('clientName'),
            serviceName: serviceData?.name || data.serviceName || 'Service',
            totalPrice,
            servicePrice: serviceData?.price || basePrice,
            serviceData,
            deposit: {
              ...data.deposit,
              baseAmount: depositBaseAmount,
              total: depositTotal,
              status: depositStatus
            },
            depositPaid,
            depositStatus,
            depositAmount: depositTotal,
            balancePaid: correctedPaymentStatus === 'paid',
            // Preserve server-set paymentStatus (e.g., 'deposit_paid') to avoid prematurely marking as paid
            paymentStatus: data.paymentStatus ?? correctedPaymentStatus,
            remainingBalance,
            priceBreakdown,
            addons: processedAddons,
            selectedAddons: processedAddons,
            addonsTotal,
            paymentMethod: paymentMethodToUse,
            taxAmount: baseTax,
            taxRate: serviceTaxRate,
            balanceTax: remainingTax,
            taxSettings,
            taxBreakdown: priceBreakdown,
            storageLocation: 'company'
          };

          setPaymentMethod(paymentMethodToUse);
          }
      }

      // If not found in active appointments, check archives
      if (!appointmentData) {
          const archiveSnap = await resolveClientPortalArchiveAppointmentSnapshot(
            companyId,
            appointmentId,
            clientScope
          );

          if (archiveSnap && archiveSnap.exists()) {
            const data = archiveSnap.data();
            
            // Convert dates
            const dateTime = convertToDate(data.dateTime);
            if (dateTime) {

            // For archived appointments, set appropriate values
            appointmentData = {
              id: archiveSnap.id,
              ...data,
              dateTime,
              companyId: companyId,
              employeeId: data.employeeId || data.employee?.id,
              employeeName: data.employeeName || data.employee?.name || 'Staff Member',
              clientId: data.clientId || localStorage.getItem('clientId'),
              clientName: data.clientName || 
                `${data.clientFirstName || ''} ${data.clientLastName || ''}`.trim() ||
                localStorage.getItem('clientName'),
              serviceName: data.serviceName || 'Service',
              totalPrice: data.totalPrice || data.servicePrice || 0,
              servicePrice: data.servicePrice || 0,
              deposit: {
                amount: data.deposit?.amount || 0,
                status: 'paid',

              },
              depositPaid: true,
              depositStatus: 'paid',
              balancePaid: true,
              paymentStatus: 'paid',
              remainingBalance: 0,
              addonsTotal: (data.selectedAddons || []).reduce((sum, addon) => sum + (addon.price || 0), 0),
              paymentMethod: data.paymentMethod || 'unknown',
              isArchived: true,
              status: data.status || 'archived',
              storageLocation: 'company'
            };
            }
          }
      }

      if (!appointmentData) {
        const pendingIdFromQuery = new URLSearchParams(location.search).get('pendingAppointmentId');
        const pendingFallback = await fetchPendingAppointment(
          companyId,
          pendingIdFromQuery || appointmentId
        );
        if (pendingFallback) {
          appointmentData = pendingFallback;
        } else {
          throw new Error('Appointment not found in either active appointments or archives');
        }
      }

      // Set staff details directly from the appointment data
      let staffData = null;
      
      if (appointmentData.employeeName) {
        // Format to show only first name in client portal
        const formattedName = formatEmployeeNameForClient({
          name: appointmentData.employeeName,
          firstName: appointmentData.employee?.firstName,
          lastName: appointmentData.employee?.lastName
        });
        staffData = {
          name: formattedName,
          title: appointmentData.employeeTitle || 'Staff Member',
          isOwner: appointmentData.employee?.isOwner || false
        };
      }

      setAppointment(appointmentData);
      setStaffDetails(staffData);
      setLoading(false);
    } catch (err) {
      const permissionDenied = err?.code === 'permission-denied' || /Missing or insufficient permissions/i.test(err?.message || '');
      if (permissionDenied) {
        try {
          const companyId = await getCompanyIdForPortal();
          const pendingIdFromQuery = new URLSearchParams(location.search).get('pendingAppointmentId');
          const pendingFallback = await fetchPendingAppointment(
            companyId,
            pendingIdFromQuery || appointmentId
          );
          if (pendingFallback) {
            setAppointment(pendingFallback);
            setStaffDetails({
              name: formatEmployeeNameForClient({
                name: pendingFallback.employeeName,
                firstName: pendingFallback.employee?.firstName,
                lastName: pendingFallback.employee?.lastName,
              }),
              title: pendingFallback.employeeTitle || 'Staff Member',
              isOwner: pendingFallback.employee?.isOwner || false,
            });
            setPaymentMethod(pendingFallback.paymentMethod || 'authorize');
            setLoading(false);
            return;
          }
        } catch (fallbackError) {
          console.error('Pending appointment fallback failed:', fallbackError);
        }
      }
      console.error('Error fetching appointment:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!storefrontClient?.email && !storefrontClient?.id) {
      return;
    }
    fetchAppointment();
  }, [appointmentId, storefrontClient?.id, storefrontClient?.email]);

  useEffect(() => {
    if (!appointment || loading || !companySlug) return;
    const segment = getAppointmentUrlSegment(appointment);
    if (!segment || segment === appointmentId) return;
    if (isFirestoreAppointmentDocId(appointmentId) && !isFirestoreAppointmentDocId(segment)) {
      const targetPath = getStorefrontAppointmentDetailPath(companySlug, segment);
      if (location.pathname !== targetPath) {
        navigate(`${targetPath}${location.search}`, { replace: true });
      }
    }
  }, [appointment, loading, companySlug, appointmentId, location.pathname, location.search, navigate]);


  useEffect(() => {
    // Set the flag when we're in appointment details
    localStorage.setItem('paymentFromAppointmentDetails', 'true');
    
    // Store the appointment ID
    if (appointmentId) {
      localStorage.setItem('currentAppointmentId', appointmentId);
    }

    // Cleanup when component unmounts
    return () => {
      // Only clean up if we're not navigating to cancel or success pages
      if (!location.pathname.includes('/cancel') && !location.pathname.includes('/success')) {
        localStorage.removeItem('paymentFromAppointmentDetails');
        localStorage.removeItem('currentAppointmentId');
      }
    };
  }, [appointmentId, location.pathname]);

  useEffect(() => {
    if (appointment?.status === 'completed' && !appointment?.hasRating) {
      setRatingDialogOpen(true);
    }
  }, [appointment]);

  const handleCloseRatingDialog = () => {
    setRatingDialogOpen(false);
  };


  // Calculate remaining balance from appointment data
  const calculatedRemainingBalance = useMemo(() => {
    if (!appointment) return 0;

    // First check if we have the balancePayment field
    if (appointment.balancePayment?.totalWithTax) {
      return parseFloat(appointment.balancePayment.totalWithTax);
    }

    // Get the base price - prioritize package price if it exists
    const basePrice = hasPackage 
      ? (getPackageDetails?.price || 0)
      : (appointment.servicePrice || appointment.price || 0);
    
    console.log('Calculating remaining balance with:', {
      hasPackage,
      packagePrice: getPackageDetails?.price,
      servicePrice: appointment.servicePrice || appointment.price,
      basePrice,
      addons: appointment.selectedAddons || []
    });
    
    // Calculate addons total
    const addonsTotal = (appointment.selectedAddons || []).reduce((sum, addon) => 
      sum + (parseFloat(addon.price) || 0), 0);
    
    // Calculate subtotal
    const subtotal = basePrice + addonsTotal;
    
    // Calculate tax
    const taxRate = appointment.taxRate || 0;
    const taxAmount = subtotal * (taxRate / 100);
    
    // Calculate total with tax
    const totalWithTax = subtotal + taxAmount;
    
    // Get deposit amount
    const depositAmount = (appointment.deposit && typeof appointment.deposit === 'object') ? 
      parseFloat(appointment.deposit.amount || 0) : 0;
    
    // Only consider deposit if it's paid
    const effectiveDepositAmount = appointment.depositStatus === 'paid' ? depositAmount : 0;
    
    // Calculate remaining balance
    const remainingBalance = Math.max(0, totalWithTax - effectiveDepositAmount);

    console.log('Final remaining balance calculation:', {
      basePrice,
      addonsTotal,
      subtotal,
      taxRate,
      taxAmount,
      totalWithTax,
      depositAmount,
      effectiveDepositAmount,
      remainingBalance
    });

    return remainingBalance;
  }, [appointment, hasPackage, getPackageDetails]);


  // Derived values for payment state
  const isDepositPaid =
    appointment?.depositStatus === 'paid' ||
    appointment?.depositStatus === 'deposit_paid' ||
    appointment?.deposit?.status === 'paid' ||
    appointment?.paymentStatus === 'deposit_paid' ||
    appointment?.depositPaid === true;
  // If deposit paid and paymentStatus not 'paid', treat as remaining balance due regardless of computed amount
  const hasRemainingBalance = isDepositPaid && appointment?.paymentStatus !== 'paid';
  const isFullyPaid = appointment?.paymentStatus === 'paid';

  if (loading) {
    return (
      <div className="appointment-details-feedback">
        <FormSkeleton fields={5} label="Loading appointment" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="appointment-details-feedback">
        <div className="appointment-alert appointment-alert-error">{error}</div>
      </div>
    );
  }
  if (!appointment) {
    return (
      <div className="appointment-details-feedback">
        <div className="appointment-alert appointment-alert-error">Appointment not found</div>
      </div>
    );
  }

  const startTime = new Date(appointment.dateTime);
  const endTime = new Date(startTime.getTime() + ((appointment.duration || 0) * 60000));
  const displayMeta = getAppointmentDisplayMeta(appointment);
  const statusClass = `appointment-chip-status--${displayMeta.cssClass}`;
  const durationLabel = (() => {
    const duration = appointment.duration || 0;
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    if (hours === 0) return `${minutes} minutes`;
    if (minutes === 0) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} minutes`;
  })();
  const appointmentTotal = (() => {
    const total = hasPackage
      ? (appointment.priceBreakdown?.baseTotal || appointment.totalPrice || getPackageDetails?.price || 0)
      : (appointment.totalPrice || 0);
    return formatCurrency(Number(parseFloat(total).toFixed(2)));
  })();

  return (
    <div className="appointment-details-page">
      <main className="appointment-details-container">
        <section className="appointment-details-shell">
          <header className="appointment-header">
            <h1 className="appointment-title">
              {hasPackage ? (getPackageDetails?.name || 'Package') : appointment.serviceName}
            </h1>

            <div className="appointment-chip-row">
              <span
                className={`appointment-chip appointment-chip-status ${statusClass}`}
              >
                {displayMeta.label}
              </span>
              {hasPackage && (
                <span className="appointment-chip">Package</span>
              )}
            </div>

            {!appointment?.isPendingRecord && (
              <div className="appointment-actions-row">
                <AppointmentActions
                  appointment={appointment}
                  companyId={appointment?.companyId}
                  companySlug={companySlug}
                  bookingSettings={companyData?.settings?.bookingSettings || {}}
                  onActionComplete={fetchAppointment}
                  isProcessing={isProcessing}
                  setIsProcessing={setIsProcessing}
                  setError={setError}
                  onReschedule={() => setRescheduleOpen(true)}
                />
              </div>
            )}
          </header>

          {appointment.status === 'cancelled' && appointment.cancelReason && (
            <section className="appointment-cancelled-card">
              <h2 className="appointment-cancelled-title">
                Cancellation Reason:
              </h2>
              <p className="appointment-cancelled-text">{appointment.cancelReason}</p>
              {appointment.cancellation?.timestamp && (
                <p className="appointment-cancelled-date">
                  Cancelled on {format(new Date(appointment.cancellation.timestamp.seconds * 1000), 'MMM d, yyyy h:mm a')}
                </p>
              )}
            </section>
          )}

          <section className="appointment-details-grid">
            <article className="appointment-detail-card">
              <p className="appointment-detail-label">Staff</p>
              <p className="appointment-detail-value">
                {staffDetails ? formatEmployeeNameForClient(staffDetails) : 'Staff Member'}
              </p>
            </article>
            <article className="appointment-detail-card">
              <p className="appointment-detail-label">Date & Time</p>
              <p className="appointment-detail-value">{format(startTime, 'EEEE, MMMM d, yyyy')}</p>
              <p className="appointment-detail-subvalue">{`${format(startTime, 'h:mm a')} - ${format(endTime, 'h:mm a')}`}</p>
            </article>
            <article className="appointment-detail-card">
              <p className="appointment-detail-label">Duration</p>
              <p className="appointment-detail-value">{durationLabel}</p>
            </article>
            <article className="appointment-detail-card">
              <p className="appointment-detail-label">{hasPackage ? 'Package Total' : 'Total Price'}</p>
              <p className="appointment-detail-value">{appointmentTotal}</p>
            </article>
          </section>

          {hasPackage ? (
            <PackageDetails
              appointment={appointment}
              getPackageDetails={getPackageDetails}
            />
          ) : (
            <ServiceDetails appointment={appointment} />
          )}

          <PaymentSummary
            appointment={appointment}
            hasPackage={hasPackage}
            getPackageDetails={getPackageDetails}
          />

          {(() => {
          const remainingBalance = parseFloat(calculatedRemainingBalance || 0);
          
          // First check if appointment is canceled
          if (appointment.status === 'cancelled') {
            return (
              <div className="appointment-status-alert appointment-status-error">
                This appointment has been cancelled. {isDepositPaid ? 'A deposit payment was made.' : 'No payments were processed.'}
              </div>
            );
          }

          // If a deposit has been paid but paymentStatus isn't 'paid', always show the
          // remaining balance banner regardless of a zero computed remainder.
          if (appointment.depositStatus === 'paid' && appointment.paymentStatus !== 'paid') {
            return (
              <div className="appointment-status-alert appointment-status-info">
                {hasPackage ? (
                  <>
                    Deposit has been paid for your package. The remaining balance of {(() => {
                      const rem = paymentMethod === 'authorize'
                        ? ((appointment.balancePayment?.totalWithTax != null)
                            ? parseFloat(appointment.balancePayment.totalWithTax)
                            : Math.max(0,
                                parseFloat(appointment.totalPrice || appointment.priceBreakdown?.baseTotal || 0) -
                                parseFloat(appointment.deposit?.amount || appointment.deposit?.total || 0)
                              ))
                        : parseFloat(calculatedRemainingBalance || 0);
                      return formatCurrency(rem);
                    })()} is due before or at the time of service.
                  </>
                ) : (
                  <>
                    Deposit has been paid. The remaining balance of {(() => {
                      const rem = paymentMethod === 'authorize'
                        ? ((appointment.balancePayment?.totalWithTax != null)
                            ? parseFloat(appointment.balancePayment.totalWithTax)
                            : Math.max(0,
                                parseFloat(appointment.totalPrice || appointment.priceBreakdown?.baseTotal || 0) -
                                parseFloat(appointment.deposit?.amount || appointment.deposit?.total || 0)
                              ))
                        : parseFloat(calculatedRemainingBalance || 0);
                      return formatCurrency(rem);
                    })()} is due before or at the time of service.
                  </>
                )}
              </div>
            );
          } else if (appointment.paymentStatus === 'paid' || remainingBalance <= 0.01) {
            return (
              <div className="appointment-status-alert appointment-status-success">
                {hasPackage ? (
                  <>
                    All payments have been completed for your package appointment.
                  </>
                ) : (
                  <>
                    All payments have been completed for this appointment.
                  </>
                )}
              </div>
            );
          } else {
            // Only show the deposit warning if a non-zero deposit is actually configured.
            const rawDepositAmount = appointment.deposit?.amount ?? appointment.deposit?.total ?? 0;
            const depositAmountNumber = parseFloat(rawDepositAmount) || 0;
            const hasRequiredDeposit = depositAmountNumber > 0;

            if (!hasRequiredDeposit) {
              // In-store / non-payment bookings (no deposit configured) should not see a deposit warning.
              return null;
            }

            return (
              <div className="appointment-status-alert appointment-status-warning">
                {hasPackage ? (
                  <>
                    Deposit payment of {formatCurrency(depositAmountNumber)} is required to confirm your package appointment.
                  </>
                ) : (
                  <>
                    Deposit payment is required to confirm this appointment.
                  </>
                )}
              </div>
            );
          }
        })()}

          {appointment.notes && (
            <section className="appointment-notes-section">
              <h2 className="appointment-notes-title">Notes</h2>
              <p className="appointment-notes-content">
                {typeof appointment.notes === 'string'
                  ? appointment.notes
                  : (appointment.notes?.notes || '')}
              </p>
            </section>
          )}

          {!isFullyPaid && appointment.status !== 'cancelled' && (() => {
            const paymentContent = (() => {
                // Debug log to identify package detection issues
                console.log('Package detection:', {
                  hasPackage,
                  packageName: getPackageDetails?.name,
                  packagePrice: getPackageDetails?.price,
                  remainingBalance: parseFloat(calculatedRemainingBalance || 0),
                  paymentMethod,
                  isFullyPaid,
                  status: appointment.status
                });

                if (isDepositPaid && !isFullyPaid && hasRemainingBalance && paymentMethod === 'authorize') {
                  const significantRemainingBalance = parseFloat(calculatedRemainingBalance || 0) > 0.01;
                  const displayedRemaining = (appointment.balancePayment?.totalWithTax != null)
                    ? parseFloat(appointment.balancePayment.totalWithTax)
                    : Math.max(0,
                        parseFloat(appointment.totalPrice || appointment.priceBreakdown?.baseTotal || 0) -
                        parseFloat(appointment.deposit?.amount || appointment.deposit?.total || 0)
                      );

                  if (!significantRemainingBalance && displayedRemaining <= 0.01) {
                    return (
                      <div className="appointment-status-alert appointment-status-success">
                        Your payment has been processed. There is no remaining balance to pay.
                      </div>
                    );
                  }

                  return (
                    <DeluxeRemainingBalance
                      appointment={appointment}
                      remainingBalance={displayedRemaining}
                      companyUrl={companyUrl}
                      onError={(e) => setPaymentError(e?.message || 'Balance payment error')}
                      disabled={isLoading}
                    />
                  );
                }

                return null;
              })();

            if (!paymentContent) {
              return null;
            }

            return (
              <section className="appointment-payment-wrap">
                {paymentError ? (
                  <div className="appointment-alert appointment-alert-error">{paymentError}</div>
                ) : null}
                {paymentContent}
              </section>
            );
          })()}

          {ratingDialogOpen && (
            <div className="appointment-modal-overlay" role="dialog" aria-modal="true">
              <div className="appointment-modal-card">
                <button
                  type="button"
                  className="appointment-modal-close"
                  onClick={handleCloseRatingDialog}
                  aria-label="Close rating dialog"
                >
                  x
                </button>
                <EmployeeRating
                  appointment={appointment}
                  onRatingSubmit={() => {
                    fetchAppointment();
                    handleCloseRatingDialog();
                  }}
                />
              </div>
            </div>
          )}

          <RescheduleDialog
            open={rescheduleOpen}
            onClose={() => setRescheduleOpen(false)}
            appointment={appointment}
            onReschedule={() => {
              setRescheduleOpen(false);
              fetchAppointment();
            }}
            onError={(msg) => setError(msg)}
          />

          {appointment?.status === 'completed' && appointment?.hasRating && appointment?.ratingData && (
            <section className="appointment-rating-wrap">
              <div className="appointment-rating-card">
                <h2 className="appointment-rating-title">Your Rating</h2>
                <div className="appointment-rating-row">
                  <span className="appointment-rating-stars">
                    {'★'.repeat(Math.max(0, Math.min(5, Math.round(appointment.ratingData.rating || 0))))}
                    {'☆'.repeat(Math.max(0, 5 - Math.round(appointment.ratingData.rating || 0)))}
                  </span>
                  <span className="appointment-rating-value">
                    {appointment.ratingData.rating} out of 5 stars
                  </span>
                </div>
                <p className="appointment-rating-comment">
                  "{appointment.ratingData.comment}"
                </p>
                <p className="appointment-rating-date">
                  Submitted on {toRatingDate(appointment.ratingData.createdAt)
                    ? format(toRatingDate(appointment.ratingData.createdAt), 'MMM d, yyyy')
                    : 'Date unavailable'}
                </p>
              </div>
            </section>
          )}

        </section>
      </main>
    </div>
  );
};

export default AppointmentDetails;